<template>
  <div v-if="product" class="mt-5">
    <UiBreadcrumb />
    <div class="mt-8 flex flex-col justify-center md:mb-14 md:flex-row md:justify-between">
      <div class="mb-6 md:mr-8">
        <UiGallery :images="galleryImages" />
      </div>
      <div class="flex flex-col md:w-1/2">
        <UiHeading type="d3">{{ product.name }}</UiHeading>
        <div v-if="currentAvailability" class="mt-2 flex">
          <UiTag
            :text="currentAvailability.on_stock ? $t('product.inStock') : $t('product.outOfStock')"
            :status-ok="currentAvailability.on_stock"
          />
        </div>
        <div class="my-6">
          <UiText type="p">
            {{ descriptionText }}
            <UiButton id="product-see-more" variant="none" class="!text-primary-500" @click="toggleText">
              {{ isExpanded ? $t('general.seeLess') : $t('general.seeMore') }}
            </UiButton>
          </UiText>
        </div>
        <ProductPrice :product="product" :variant-sku="productStore.selectedVariantSku" price-class="!text-2xl" />
        <ProductSelectVariant />
        <ProductPurchase />
        <UiDivider class="mb-6" />
        <UiText>{{ $t('product.sku', { sku: selectedVariant?.sku ?? product.sku }) }}</UiText>
      </div>
    </div>
    <ProductDetail />
    <ProductSlider
      v-if="product.associated_products"
      id="associated-products"
      class="mt-10"
      :products="product.associated_products"
      :title="$t('product.relatedProductsTitle')"
      :subtitle="$t('product.relatedProductsSubtitle')"
      :show-arrows="false"
    />
  </div>
</template>

<script setup lang="ts">
import type { ProductContentProps } from '~/components/Product/types';
import type { GalleryImage } from '~/components/ui/Gallery/types';

import { useProductStore } from '~/stores/product/productStore';

const productStore = useProductStore();

const isExpanded = ref<boolean>(false);

const props = defineProps<ProductContentProps>();

const selectedVariant = computed(() => {
  if (!productStore.selectedVariantSku || !props.product?.variants) return null;
  return props.product.variants.find((v) => v.sku === productStore.selectedVariantSku) ?? null;
});

const currentAvailability = computed(() => selectedVariant.value?.availability ?? props.product?.availability);

const galleryImages = computed<GalleryImage[]>(() => {
  const images = selectedVariant.value?.images ?? props.product?.images;
  return (
    images?.map((image: string) => ({
      label: 'image',
      src: image,
    })) ?? []
  );
});

const descriptionText = computed(() => {
  return props.product?.description
    ? isExpanded.value
      ? props.product.description
      : `${props.product.description.slice(0, 100)}...`
    : '';
});

const toggleText = (): void => {
  isExpanded.value = !isExpanded.value;
};
</script>
